import React, { useState, ChangeEvent } from "react";
import { toast } from "react-toastify";
import Title from "../../../components/Admin/Title";
import { Form, Button, Col, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../../components/UserContext";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";

export default function UploadShort() {
  const { user } = useUser() as any;
  const token = window.localStorage.getItem("userLogged");
  const [video, setVideo] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [validated, setValidated] = useState(false);

  // Estados específicos para el Bloque 1
  const [creatorInfoLoading, setCreatorInfoLoading] = useState<boolean>(false);
  const [creatorNickname, setCreatorNickname] = useState<string | null>(null);
  const [maxDuration, setMaxDuration] = useState<number | null>(null);
  const [canPost, setCanPost] = useState<boolean>(true);
  const [postLimitMessage, setPostLimitMessage] = useState<string>("");

  // Estados específicos para el Bloque 2
  const [privacyOptions, setPrivacyOptions] = useState<string[]>([]);
  const [privacyLevel, setPrivacyLevel] = useState<string>("");
  const [disableComment, setDisableComment] = useState<boolean>(false);
  const [disableDuet, setDisableDuet] = useState<boolean>(false);
  const [disableStitch, setDisableStitch] = useState<boolean>(false);
  const [commentRestricted, setCommentRestricted] = useState<boolean>(false);
  const [duetRestricted, setDuetRestricted] = useState<boolean>(false);
  const [stitchRestricted, setStitchRestricted] = useState<boolean>(false);

  // Estados específicos para el Paso 3 (Commercial Content)
  const [isCommercial, setIsCommercial] = useState<boolean>(false);
  const [promoteYourBrand, setPromoteYourBrand] = useState<boolean>(false);
  const [promoteBrandedContent, setPromoteBrandedContent] =
    useState<boolean>(false);

  const BASE_URL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5500"
      : "https://ligavirtualf1.es:5500";

  const fetchCreatorInfo = async () => {
    if (!token) return;
    setCreatorInfoLoading(true);

    try {
      const parsedToken = JSON.parse(token);
      const response = await fetch(
        `${BASE_URL}/api/admin/tiktok-creator-info?user=${user}&token=${parsedToken.token}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (response.status === 401) {
        setCanPost(false);
        setPostLimitMessage(
          "TikTok authentication required. Please try uploading to trigger login.",
        );
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCreatorNickname(
            data.creator_nickname || "Cuenta de TikTok vinculada",
          );
          setMaxDuration(data.max_video_post_duration_sec || 60);

          if (
            data.privacy_level_options &&
            Array.isArray(data.privacy_level_options)
          ) {
            setPrivacyOptions(data.privacy_level_options);
          } else {
            setPrivacyOptions([
              "PUBLIC_TO_EVERYONE",
              "MUTUAL_FOLLOW_FRIENDS",
              "SELF_ONLY",
            ]);
          }

          if (data.comment_disabled) setCommentRestricted(true);
          if (data.duet_disabled) setDuetRestricted(true);
          if (data.stitch_disabled) setStitchRestricted(true);

          if (data.is_creator_allowed === false || data.reach_limit) {
            setCanPost(false);
            setPostLimitMessage(
              "You have reached your posting limit at this moment. Please try again later.",
            );
          } else {
            setCanPost(true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching creator info:", error);
    } finally {
      setCreatorInfoLoading(false);
    }
  };

  React.useEffect(() => {
    fetchCreatorInfo();
  }, []);

  // Capturar archivo, validar duración y generar Previsualización (Paso 5)
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const videoElement = document.createElement("video");
      videoElement.preload = "metadata";

      videoElement.onloadedmetadata = () => {
        window.URL.revokeObjectURL(videoElement.src);
        const duration = videoElement.duration;

        if (maxDuration && duration > maxDuration) {
          toast.error(
            `Video duration exceeds the maximum allowed limit of ${maxDuration} seconds.`,
          );
          setVideo(null);
          setVideoPreviewUrl(null);
          e.target.value = "";
        } else {
          setVideo(selectedFile);
          setVideoPreviewUrl(URL.createObjectURL(selectedFile));
        }
      };

      videoElement.src = URL.createObjectURL(selectedFile);
    }
  };

  const handleSubmit = async (e: any) => {
    if (token == null) return;
    e.preventDefault();

    const form = e.currentTarget;
    setValidated(true);

    if (!privacyLevel) {
      toast.error("Please select a privacy status.");
      return;
    }

    // Validación de contenido comercial (Paso 3)
    if (isCommercial && !promoteYourBrand && !promoteBrandedContent) {
      toast.error(
        "Please select at least one commercial content option (Your brand or Branded content).",
      );
      return;
    }

    if (!form.checkValidity() || !canPost) {
      return;
    }

    if (!video) {
      toast.error("Video is required");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("video", video);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("privacy_level", privacyLevel);
    formData.append("disable_comment", String(disableComment));
    formData.append("disable_duet", String(disableDuet));
    formData.append("disable_stitch", String(disableStitch));

    // Datos comerciales (Paso 3)
    formData.append("is_commercial", String(isCommercial));
    formData.append("brand_organic_toggle", String(promoteYourBrand));
    formData.append("branded_content_toggle", String(promoteBrandedContent));

    formData.append("user", user);
    formData.append("token", JSON.parse(token).token);

    try {
      const response = await fetch(`${BASE_URL}/api/admin/upload-short`, {
        method: "POST",
        body: formData,
      });

      if (response.status === 401) {
        const data = await response.json();
        const authWindow = window.open(
          data.authUrl,
          "_blank",
          "width=600,height=700",
        );

        const timer = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(timer);
            toast.warning(
              "TikTok authentication complete. Please try uploading again.",
            );
            fetchCreatorInfo();
          }
        }, 1000);
      } else if (response.ok) {
        const data = await response.json();

        if (data.success) {
          toast.success(
            "Short published successfully! Note: It may take a few minutes to process and appear on your TikTok profile.",
          );
          setVideo(null);
          setVideoPreviewUrl(null);
          setTitle("");
          setDescription("");
          setPrivacyLevel("");
          setDisableComment(false);
          setDisableDuet(false);
          setDisableStitch(false);
          setIsCommercial(false);
          setPromoteYourBrand(false);
          setPromoteBrandedContent(false);
          setValidated(false);
          fetchCreatorInfo();
        } else {
          toast.error(`Error: ${data.error || "Failed to upload reels"}`);
        }
      }
    } catch (error) {
      console.error("Error connecting to the server:", error);
      toast.error("An error occurred while connecting to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Upload TikTok Reel" />
        <small className="mandatory-fields">(*) Required fields</small>

        <div className="mb-3 p-3 bg-light rounded border">
          <span className="text-muted">
            Target TikTok Account:{" "}
            <strong>
              {creatorInfoLoading
                ? "Loading account info..."
                : creatorNickname || "Not connected"}
            </strong>
          </span>
        </div>

        {!canPost && <Alert variant="warning">{postLimitMessage}</Alert>}

        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {/* Título y descripción totalmente editables (Paso 5) */}
          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Title (*)</Form.Label>
            <Form.Control
              type="text"
              required
              value={title}
              placeholder="Edit title and hashtags freely..."
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Hashtags (*)</Form.Label>
            <Form.Control
              type="text"
              required
              value={description}
              placeholder="Edit hashtags..."
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
          </Form.Group>

          {/* Privacy Status Dropdown (Bloqueado a SELF_ONLY si marca Branded Content) */}
          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Privacy Status (*)</Form.Label>
            <Form.Select
              required
              value={privacyLevel}
              onChange={(e) => setPrivacyLevel(e.target.value)}
            >
              <option value="">-- Select privacy status --</option>
              {privacyOptions.map((option) => {
                const isDisabledForBranded =
                  promoteBrandedContent && option === "SELF_ONLY";
                return (
                  <option
                    key={option}
                    value={option}
                    disabled={isDisabledForBranded}
                  >
                    {option === "PUBLIC_TO_EVERYONE" && "Public to everyone"}
                    {option === "MUTUAL_FOLLOW_FRIENDS" && "Friends only"}
                    {option === "SELF_ONLY" && "Only me (Private)"}
                    {![
                      "PUBLIC_TO_EVERYONE",
                      "MUTUAL_FOLLOW_FRIENDS",
                      "SELF_ONLY",
                    ].includes(option) && option}
                    {isDisabledForBranded
                      ? " (Not allowed for Branded Content)"
                      : ""}
                  </option>
                );
              })}
            </Form.Select>
            {promoteBrandedContent && (
              <small className="text-danger d-block mt-1">
                "Only me" privacy is disabled when Branded Content is selected.
              </small>
            )}
            <Form.Control.Feedback type="invalid">
              Please select a privacy status.
            </Form.Control.Feedback>
          </Form.Group>

          {/* Interaction Settings */}
          <Form.Group className="form-group mb-3" as={Col} md="12">
            <Form.Label>Interaction Settings</Form.Label>
            <div className="d-flex flex-column gap-2 border p-3 rounded bg-white">
              <Form.Check
                type="checkbox"
                id="comment-check"
                label="Allow comments"
                checked={!disableComment}
                disabled={commentRestricted}
                onChange={(e) => setDisableComment(!e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="duet-check"
                label="Allow duets"
                checked={!disableDuet}
                disabled={duetRestricted}
                onChange={(e) => setDisableDuet(!e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="stitch-check"
                label="Allow stitch"
                checked={!disableStitch}
                disabled={stitchRestricted}
                onChange={(e) => setDisableStitch(!e.target.checked)}
              />
            </div>
          </Form.Group>

          {/* PASO 3: Commercial Content Toggle y Opciones */}
          <Form.Group
            className="form-group mb-3 border p-3 rounded bg-light"
            as={Col}
            md="12"
          >
            <Form.Check
              type="switch"
              id="commercial-content-switch"
              label="Is this commercial content?"
              checked={isCommercial}
              onChange={(e) => {
                setIsCommercial(e.target.checked);
                if (!e.target.checked) {
                  setPromoteYourBrand(false);
                  setPromoteBrandedContent(false);
                }
              }}
            />
            <small className="text-muted d-block mt-1">
              Promote a product, brand, or yourself in the video.
            </small>

            {isCommercial && (
              <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
                <Form.Check
                  type="checkbox"
                  id="your-brand-check"
                  label="Your brand (Promoting your own business)"
                  checked={promoteYourBrand}
                  onChange={(e) => setPromoteYourBrand(e.target.checked)}
                />
                {promoteYourBrand && (
                  <small className="text-info ms-4">
                    Your photo/video will be labeled as 'Promotional content'
                  </small>
                )}

                <Form.Check
                  type="checkbox"
                  id="branded-content-check"
                  label="Branded content (Promoting a third party)"
                  checked={promoteBrandedContent}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setPromoteBrandedContent(val);
                    // Si marca branded content y estaba en SELF_ONLY, cambiar a public de forma automática
                    if (val && privacyLevel === "SELF_ONLY") {
                      setPrivacyLevel("PUBLIC_TO_EVERYONE");
                    }
                  }}
                />
                {promoteBrandedContent && (
                  <small className="text-info ms-4">
                    Your photo/video will be labeled as 'Paid partnership'
                  </small>
                )}
              </div>
            )}
          </Form.Group>

          {/* Vídeo y Preview (Paso 5) */}
          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Video (*)</Form.Label>
            <Form.Control
              type="file"
              accept="video/*"
              required
              onChange={handleFileChange}
              disabled={!canPost}
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
            {maxDuration && (
              <small className="text-muted d-block mt-1">
                Max video duration allowed: {maxDuration} seconds.
              </small>
            )}
          </Form.Group>

          {/* Vista previa del vídeo (Paso 5) */}
          {videoPreviewUrl && (
            <div className="mb-3 text-center border p-2 rounded bg-light">
              <p className="text-muted small mb-2">Video Preview:</p>
              <video
                src={videoPreviewUrl}
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: "250px",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}

          {/* PASO 4: Declaración legal dinámica basada en Commercial Content */}
          <div className="p-3 mb-3 bg-light border rounded text-muted small text-center">
            {promoteBrandedContent
              ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
              : "By posting, you agree to TikTok's Music Usage Confirmation."}
          </div>

          <div className="text-center">
            {loading ? (
              <div className="d-flex flex-column align-items-center gap-2">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="spinner-button fa-spin"
                />
                <small className="text-muted">
                  Uploading and processing video... Please note it may take a
                  few minutes to appear on your TikTok profile.
                </small>
              </div>
            ) : (
              <Button variant="primary" type="submit" disabled={!canPost}>
                {"Upload Video to "}
                <FontAwesomeIcon icon={faTiktok} />
                {" TikTok"}
              </Button>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
}
